// Tenantra Inc. - Tenant panel Billing: balance owing, lease charges (move-in and extra), recent payments, add or cancel a charge
import { useCallback, useEffect, useState } from 'react';
import { Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Section, fmtDate } from './DetailPanel';
import { Pill } from './ScreeningAddresses';
import { money, PAY_STATUS, METHOD } from './payments';

const TYPES = [['late_fee', 'Late fee'], ['utilities', 'Utilities'], ['parking', 'Parking'], ['repair', 'Repair or damage'], ['other', 'Other']];
const CHARGE = { due: ['Due', 'amber'], pending: ['Processing', 'amber'], paid: ['Paid', 'emerald'] };
const blank = () => ({ type: 'late_fee', label: '', amount: '', due_date: new Date().toLocaleDateString('en-CA') });
const monthName = (m) => new Date(`${m}-15`).toLocaleDateString('en-CA', { month: 'long' });

function AddChargeDialog({ open, onClose, tenancyId, collecting, onAdded }) {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const ok = Number(f.amount) > 0 && f.due_date && (f.type !== 'other' || f.label.trim());
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/landlord/tenancies/${tenancyId}/charges`, { type: f.type, label: f.type === 'other' ? f.label.trim() : null, amount: Number(f.amount), due_date: f.due_date });
      toast.success('Charge added'); setF(blank()); onAdded(); onClose();
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm bg-white" data-testid="add-charge-dialog">
        <DialogHeader>
          <DialogTitle>Add a charge</DialogTitle>
          <DialogDescription>{collecting ? 'The tenant sees it in their portal and can pay it online.' : 'The tenant sees it in their portal. Online payment starts once your rent wallet is active.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Type</Label>
            <Select value={f.type} onValueChange={(v) => set('type', v)}>
              <SelectTrigger className="h-10 mt-1" data-testid="charge-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`charge-type-${k}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {f.type === 'other' && <div><Label htmlFor="charge-label" className="text-xs">Charge name</Label><Input id="charge-label" value={f.label} maxLength={60} onChange={(e) => set('label', e.target.value)} className="h-10 mt-1" data-testid="charge-label" /></div>}
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="charge-amount" className="text-xs">Amount (CAD)</Label><Input id="charge-amount" inputMode="decimal" value={f.amount} onChange={(e) => set('amount', e.target.value.replace(/[^\d.]/g, ''))} className="h-10 mt-1" data-testid="charge-amount" /></div>
            <div><Label htmlFor="charge-due" className="text-xs">Due date</Label><Input id="charge-due" type="date" value={f.due_date} onChange={(e) => set('due_date', e.target.value)} className="h-10 mt-1" data-testid="charge-due-date" /></div>
          </div>
          <button onClick={submit} disabled={busy || !ok} className="w-full h-10 rounded-md bg-teal-600 hover:bg-teal-700 transition-colors text-white text-sm font-medium disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="charge-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add {Number(f.amount) ? money(f.amount) : 'charge'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChargeRow({ c, pay, confirming, onAsk, onCancel, onKeep }) {
  const st = c.status === 'pending' && pay ? PAY_STATUS[pay.status] || CHARGE.pending : CHARGE[c.status] || [c.status, 'slate'];
  const sub = c.status === 'paid' && c.paid_at ? `Paid ${fmtDate(c.paid_at)}` : c.status === 'pending' && c.paid_at ? `Payment sent ${fmtDate(c.paid_at)}` : `Due ${fmtDate(c.due_date)}`;
  return (
    <li className="px-3 py-2.5 text-sm" data-testid={`billing-charge-${c.id || c.type}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0"><p className="text-slate-800 truncate">{c.label}</p><p className="text-xs text-slate-500">{sub}</p></div>
        <span className="tabular-nums text-slate-800 flex-shrink-0">{money(c.amount)}</span>
        <span className="flex-shrink-0"><Pill tone={st[1]} testId={`billing-charge-status-${c.id || c.type}`}>{st[0]}</Pill></span>
        {c.source === 'landlord' && c.status === 'due' && !confirming && (
          <button onClick={onAsk} aria-label={`Cancel ${c.label}`} className="w-6 h-6 inline-flex items-center justify-center rounded-full text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`billing-charge-cancel-${c.id}`}><X className="w-3.5 h-3.5" /></button>
        )}
      </div>
      {confirming && (
        <div className="flex items-center gap-3 mt-2 text-xs" data-testid={`billing-charge-confirm-${c.id}`}>
          <span className="text-slate-600">Cancel this charge?</span>
          <button onClick={onCancel} className="font-medium text-red-600 hover:text-red-700" data-testid={`billing-charge-confirm-yes-${c.id}`}>Yes, cancel</button>
          <button onClick={onKeep} className="text-slate-500 hover:text-slate-800">Keep</button>
        </div>
      )}
    </li>
  );
}

export function TenantBilling({ tenancyId }) {
  const [b, setB] = useState(null);
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => api.get(`/landlord/tenancies/${tenancyId}/billing`).then(r => setB(r.data)).catch(() => setB(false)), [tenancyId]);
  useEffect(() => { setB(null); load(); }, [load]);
  const cancel = (c) => api.delete(`/landlord/tenancies/${tenancyId}/charges/${c.id}`)
    .then(() => { toast.success('Charge cancelled'); setConfirm(null); load(); }).catch(e => toast.error(apiHelpers.handleError(e)));
  if (b === false) return null;
  const rentSt = b?.rent ? PAY_STATUS[b.rent.status] || [b.rent.status, 'slate'] : null;
  return (
    <Section title="Billing" aside={b?.tenancy_status === 'active' && <button onClick={() => setAdding(true)} className="text-xs font-medium text-teal-700 hover:text-teal-800 inline-flex items-center gap-1" data-testid="billing-add-charge"><Plus className="w-3.5 h-3.5" /> Add charge</button>}>
      {!b ? <div className="h-16 bg-slate-100 rounded animate-pulse" /> : <div className="space-y-4" data-testid="tenant-billing">
        <div>
          <p className="text-[11px] text-slate-400">Balance owing</p>
          <p className={`text-2xl font-semibold tabular-nums ${b.balance > 0 ? 'text-slate-800' : 'text-emerald-600'}`} data-testid="billing-balance">{money(b.balance)}</p>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="billing-balance-breakdown">
            {b.collecting ? `Rent ${money(b.rent_due)} · Charges ${money(b.charges_due)}` : `Charges ${money(b.charges_due)}. Rent isn't collected through Tenantra yet.`}
          </p>
        </div>
        {b.collecting && b.rent && (
          <div className="flex items-center justify-between gap-3 text-sm" data-testid="billing-rent">
            <div><p className="text-slate-800">{monthName(b.month)} rent</p><p className="text-xs text-slate-500 tabular-nums">{money(b.rent.paid)} of {money(b.rent.rent)} · due {fmtDate(b.rent.due_date)}</p></div>
            <Pill tone={rentSt[1]} testId="billing-rent-status">{rentSt[0]}</Pill>
          </div>
        )}
        <div>
          <p className="text-[11px] text-slate-400 mb-1.5">Charges</p>
          {b.charges.length === 0 ? <p className="text-sm text-slate-500" data-testid="billing-no-charges">No charges on this lease.</p> : (
            <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="billing-charges">
              {b.charges.map(c => <ChargeRow key={c.id || c.type} c={c} pay={b.payments.find(p => p.id === c.payment_id)} confirming={confirm === c.id} onAsk={() => setConfirm(c.id)} onCancel={() => cancel(c)} onKeep={() => setConfirm(null)} />)}
            </ul>
          )}
        </div>
        <div>
          <p className="text-[11px] text-slate-400 mb-1.5">Recent payments</p>
          {b.payments.length === 0 ? <p className="text-sm text-slate-500" data-testid="billing-no-payments">No online payments yet.</p> : (
            <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="billing-payments">
              {b.payments.slice(0, 6).map(p => { const st = PAY_STATUS[p.status] || [p.status, 'slate']; return (
                <li key={p.id} className="px-3 py-2.5 flex items-center gap-3 text-sm" data-testid={`billing-payment-${p.id}`}>
                  <div className="flex-1 min-w-0"><p className="text-slate-800 truncate">{p.label}</p><p className="text-xs text-slate-500">{fmtDate(p.created_at)} · {METHOD[p.method] || p.method}</p></div>
                  <span className="tabular-nums text-slate-800 flex-shrink-0">{money(p.amount)}</span>
                  <span className="flex-shrink-0"><Pill tone={st[1]}>{st[0]}</Pill></span>
                </li>
              ); })}
            </ul>
          )}
        </div>
      </div>}
      <AddChargeDialog open={adding} onClose={() => setAdding(false)} tenancyId={tenancyId} collecting={b?.collecting} onAdded={load} />
    </Section>
  );
}
